import { Component, h, Event, EventEmitter, Prop, State } from '@stencil/core';
import { presetAttributes } from '../../../../interfaces';
import { lightOrDark } from '../../../../util';

@Component({
  tag: 'presto-audio',
  styleUrl: 'presto-audio.scss',
  shadow: false,
})
export class PrestoAudio {
  private el: HTMLDivElement;
  @Prop({ mutable: true }) getRef?: (elm?: HTMLAudioElement) => void;
  @Prop() autoplay: boolean;
  @Prop() src: string;
  @Prop() preload: string;
  @Prop() poster: string;
  @Prop() player: any;
  @Prop() preset: presetAttributes;
  @Prop() tracks: { label: string; src: string; srcLang: string }[];
  @Prop() provider: string;
  @Prop() mediaTitle: string;
  @Prop() audioAttributes: object;

  @Event() playVideo: EventEmitter<void>;
  @Event() pauseVideo: EventEmitter<true>;

  @State() width: number;

  renderPosterImage() {
    if (this.poster) {
      return (
        <div class="presto-audio__poster">
          <img src={this.poster} />
        </div>
      );
    }
    return;
  }

  hasPosterArea() {
    return !!this.poster || !!this.preset?.['play-large'];
  }

  renderMobilePoster() {
    return (
      <div class="presto-audio__poster-wrapper-mobile">
        {!!this.preset?.['play-large'] && this.renderLargePlay('presto-audio__large-play-button is-relative')}
        <div class="presto-audio__mobile-title">{this.mediaTitle}</div>
      </div>
    );
  }

  renderLargePlay(className = 'presto-audio__large-play-button') {
    return (
      <div
        class={className}
        onClick={() => {
          if (!this.player.playing) {
            this.playVideo.emit();
          } else {
            this.pauseVideo.emit();
          }
        }}
      >
        <svg class="presto-audio__icon-play" width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5588 9.00005L0.117662 17.915L0.117662 0.0850823L15.5588 9.00005Z" />
        </svg>
        <svg class="presto-audio__icon-pause" width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="5" height="17" />
          <rect x="12" width="5" height="17" />
        </svg>
      </div>
    );
  }

  componentDidLoad() {
    // Only run if ResizeObserver is supported.
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(entries => {
        entries.forEach(entry => (this.width = entry.contentRect.width));
      });
      ro.observe(this.el);
    }
  }

  render() {
    return (
      <div
        ref={el => (this.el = el as HTMLDivElement)}
        class={{
          'presto-audio__wrapper': true,
          'has-poster': !!this.poster,
          'has-play-large': !!this.preset?.['play-large'],
          'is-playing': this.player?.playing,
          'has-light-background': lightOrDark(this.preset?.background_color) === 'light',
          'has-dark-background': lightOrDark(this.preset?.background_color) === 'dark',
          'breakpoint-small': this.width < 520,
          'skin-stacked': this.width < 520,
          'breakpoint-large': this.width >= 520,
        }}
      >
        {this.hasPosterArea() && (
          <div class="presto-audio__poster-wrapper">
            {!!this.preset?.['play-large'] && <div class="presto-audio__large-play-wrapper">{this.renderLargePlay()}</div>}
            {this.renderPosterImage()}
          </div>
        )}

        {this.hasPosterArea() && this.renderMobilePoster()}

        <div class="presto-audio__controls-wrapper">
          <div class="presto-audio__title">{this.mediaTitle}</div>
          <audio part="audio-player" ref={this.getRef} autoplay={this.autoplay} preload={this.preload} data-poster={this.poster} {...this.audioAttributes}>
            <source src={this.src} />
            {!!this.tracks &&
              !!this.tracks.length &&
              this.tracks.map(track => <track kind="captions" label={track?.label ? track.label : 'Captions'} src={track?.src} srclang={track?.srcLang ? track?.srcLang : 'en'} />)}
          </audio>
        </div>
      </div>
    );
  }
}
